import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Truck, Save, Trash2, Plus, Loader2, Zap, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function DeliveryIncentivesPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/delivery-incentives/config`, auth());
        setCfg(r.data);
      } catch (e) {
        toast.error('Failed to load delivery incentive config');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSlab = (i, field, value) => {
    const slabs = cfg.slabs.map((s, idx) => (idx === i ? { ...s, [field]: value } : s));
    setCfg({ ...cfg, slabs });
  };

  const addSlab = () => {
    const last = cfg.slabs[cfg.slabs.length - 1];
    const min = last ? (parseInt(last.max) || parseInt(last.min) || 0) + 1 : 0;
    setCfg({ ...cfg, slabs: [...cfg.slabs, { min, max: null, amount: 0 }] });
  };

  const removeSlab = (i) => setCfg({ ...cfg, slabs: cfg.slabs.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/delivery-incentives/config`, cfg, auth());
      setCfg(r.data);
      toast.success('Delivery incentive settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (!cfg) return null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="delivery-incentives-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Truck className="w-6 h-6" /> Delivery Incentives</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Automatic driver incentives — credited to the ledger during monthly payroll processing.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-delivery-incentives-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 flex items-center justify-between">
          <div>
            <p className="font-semibold">Enable Delivery Incentive Program</p>
            <p className="text-sm text-muted-foreground">
              Applies to employees with the "Delivery Incentive" type enabled in HR &gt; Incentive Settings.
            </p>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="program-enabled-switch" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Delivery Slabs</CardTitle>
          <p className="text-sm text-muted-foreground">
            The rate of the slab the driver's monthly delivered count falls into applies to ALL deliveries that month.
            E.g. 0–50 = 3/delivery, 51–100 = 5/delivery.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {cfg.slabs.map((s, i) => (
            <div key={i} className="flex items-end gap-3" data-testid={`slab-row-${i}`}>
              <div className="w-28">
                <Label className="text-xs">From (deliveries)</Label>
                <Input type="number" min="0" value={s.min ?? ''} onChange={(e) => setSlab(i, 'min', e.target.value)} data-testid={`slab-min-${i}`} />
              </div>
              <div className="w-28">
                <Label className="text-xs">To (blank = ∞)</Label>
                <Input type="number" min="0" value={s.max ?? ''} placeholder="∞" onChange={(e) => setSlab(i, 'max', e.target.value === '' ? null : e.target.value)} data-testid={`slab-max-${i}`} />
              </div>
              <div className="w-36">
                <Label className="text-xs">Amount per delivery</Label>
                <Input type="number" min="0" step="0.5" value={s.amount ?? ''} onChange={(e) => setSlab(i, 'amount', e.target.value)} data-testid={`slab-amount-${i}`} />
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeSlab(i)} data-testid={`slab-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addSlab} data-testid="add-slab-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Slab
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-500" /> Same-Day Completion Bonus</CardTitle>
          <p className="text-sm text-muted-foreground">
            Fixed bonus when a driver completes ALL assigned deliveries on the same day (no pending, failed or rescheduled orders left).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Enable same-day bonus</Label>
            <Switch checked={!!cfg.same_day_enabled} onCheckedChange={(v) => setCfg({ ...cfg, same_day_enabled: v })} data-testid="same-day-enabled-switch" />
          </div>
          <div className="flex gap-4">
            <div className="w-40">
              <Label className="text-xs">Bonus amount</Label>
              <Input type="number" min="0" step="1" value={cfg.same_day_amount ?? ''} onChange={(e) => setCfg({ ...cfg, same_day_amount: e.target.value })} data-testid="same-day-amount-input" />
            </div>
            <div className="w-56">
              <Label className="text-xs">Min deliveries to qualify</Label>
              <Input type="number" min="1" value={cfg.same_day_min_assigned ?? 1} onChange={(e) => setCfg({ ...cfg, same_day_min_assigned: e.target.value })} data-testid="same-day-min-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Prevents earning the bonus on very light days.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/40 rounded-lg p-4">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Drivers see their live progress, targets and bonus nudges on their My Deliveries page.
          Earned amounts are posted automatically to the employee ledger and salary slip when payroll is processed for the month.
        </p>
      </div>
    </div>
  );
}
